import React, { useEffect, useRef, useState } from 'react'
import { LaTercera, MtOnline } from '@labcon/copesa-logos'
import styled from '@emotion/styled'
import { rgba } from 'emotion-rgba'
import { meta } from '@/styles/typography'
import { AnimatePresence, motion } from 'framer-motion'
import { useGetResizer } from '@/contexts/Resizer'
import { useNavSize } from '@/contexts/NavSize'
import { useGetTheme } from '@/contexts/ThemingContext'
import { useGetSection } from '@/contexts/SectionContext'
import ReadProgress from '../ReadProgress'
import { Link } from 'gatsby'
import { colours } from '@/styles/colours'
import { grid, spacing, screen, blackOrWhite } from '@/styles/mixins'
import LogoMG from '@/images/vectors/logo-mg.svg'

const Container = styled.header`
  width: 100vw;
  position: fixed;
  z-index: 100;
`

const HeaderContainer = styled.div`
  /* background-color: rgba(255, 255, 255, 0.8); */
  ${spacing('paddingTop', 'm')}
  ${spacing('paddingBottom', 'm')};
  width: 100%;
  --fg: ${props => rgba(blackOrWhite(props.theme), 0.7)};
  --bg: ${props => rgba(blackOrWhite(props.theme, 'bg'), 0.7)};
  color: var(--fg);
  background-color: ${({ floating }) => (floating ? 'var(--bg)' : 'none')};
  transform: ${({ visible }) => (visible ? 'none' : 'translateY(-100%)')};
  transition: transform 300ms ease-in-out;
  position: relative;
  backdrop-filter: ${({ floating }) =>
    floating ? 'saturate(240%) blur(5px)' : 'none'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: block;
    width: 100%;
    height: 100%;
    box-shadow: 0px 0.5em 0.5em 0px rgba(0, 0, 0, 0.1);
    z-index: -1;
    transition: opacity 300ms ease-in-out;
    opacity: ${({ floating, visible }) => (floating && visible ? 1 : 0)};
  }
`

const Inner = styled.div`
  display: grid;
  padding-left: 2rem;
  padding-right: 2rem;
  width: 100%;
  grid-template-columns: 200px 1fr 220px;
  align-items: center;

  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 1rem;
    grid-template-columns: 200px 1fr;
    > div {
      &:nth-child(1) {
        svg {
          width: 140px;
        }
      }

      &:last-of-type {
        display: none;
      }
    }
  }
`

const LogoWrapper = styled.div`
  position: relative;
  svg {
    height: 1.5em;
    max-width: 100%;
    width: auto;
    path {
      transition: fill 400ms linear;
    }
  }
`

const BrandWrapper = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 2em;
    max-width: 100%;
    width: auto;
    path {
      transition: fill 400ms linear;
    }
  }
`

const Title = styled(motion.div)`
  justify-self: center;
  ${meta};
  ${spacing('paddingLeft', 'm')};
  ${spacing('paddingRight', 'm')};
  max-width: 60vw;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
  will-change: opacity;
  display: none;

  @media ${screen('md')} {
    display: block;
  }
`

const AnimatedSectionTitle = () => {
  const activeSection = useGetSection()
  return (
    <AnimatePresence exitBeforeEnter>
      <Title
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        dangerouslySetInnerHTML={{ __html: activeSection }}
        key={activeSection}
      />
    </AnimatePresence>
  )
}

const Header = () => {
  const [navigationOpen, setNavigationOpen] = useState(false)
  const ref = useRef()
  const area = useGetResizer()
  const { setHeight } = useNavSize()
  const theme = useGetTheme()
  const scrollPos = useRef(0)
  const isScrollingUp = useRef(false)
  const scrollPosOnUpStart = useRef(0)
  const [visible, setVisible] = useState(true)
  const [floating, setFloating] = useState(false)
  const threshholdToVisible = 70 // amount (px) at top of page where header always visible
  const threshholdAlwaysVisible = 50 // amount (px) user must scroll up to make header visible
  const [windowHeight, setWindowHeight] = useState(768)

  useEffect(() => {
    let rect = ref.current.getBoundingClientRect()
    setHeight(rect.height)
  }, [area, setHeight])

  useEffect(() => {
    const handleResize = () => {
      updateWindowHeight()
    }

    const updateWindowHeight = () => {
      setWindowHeight(window.innerHeight)
    }

    updateWindowHeight()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY
      const prevScrollPos = scrollPos.current
      scrollPos.current = currentScrollPos
      const direction = currentScrollPos - prevScrollPos

      if (!isScrollingUp.current && direction < 0) {
        // if started scrolling up
        isScrollingUp.current = true
        scrollPosOnUpStart.current = currentScrollPos
      } else if (direction >= 0) {
        // if scroll direction is down
        isScrollingUp.current = false
        setVisible(false)
      }

      const amountScrolledUp = scrollPosOnUpStart.current - currentScrollPos

      if (
        (isScrollingUp.current && amountScrolledUp > threshholdToVisible) ||
        currentScrollPos < threshholdAlwaysVisible
      ) {
        setVisible(true)
      } else {
        setVisible(false)
      }

      setFloating(currentScrollPos >= windowHeight)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [windowHeight])

  const handleNavigationToggle = () => {
    setNavigationOpen(!navigationOpen)
  }

  return (
    <Container ref={ref}>
      <ReadProgress />

      <HeaderContainer theme={theme} visible={visible} floating={floating}>
        <Inner>
          <LogoWrapper theme={theme} navigationOpen={navigationOpen}>
            <Link to="/">
              <MtOnline
                red="black"
                schema={blackOrWhite(navigationOpen ? 'dark' : theme)}
                width="200"
                height="20"
              />
            </Link>
          </LogoWrapper>
          <BrandWrapper theme={theme} navigationOpen={navigationOpen}>
            <Link to="/" className="text-center flex sm:flex-col">
              <small className="font-sans block pr-2 sm:pr-0 pt-2 sm:pt-0">
                Presentado por
              </small>
              <LogoMG />
            </Link>
          </BrandWrapper>
          <LogoWrapper theme={theme} navigationOpen={navigationOpen}>
            <a
              target="_blank"
              className="block"
              href="https://www.latercera.com/?utm_source=ninosmigrantes&utm_medium=interactivo&utm_campaign=logo-latercera&utm_content=logo-latercera-header">
              <LaTercera
                red="black"
                schema={blackOrWhite(navigationOpen ? 'dark' : theme)}
                width="200"
                height="20"
              />
            </a>
          </LogoWrapper>
        </Inner>
      </HeaderContainer>
    </Container>
  )
}

export default Header
